// ---------------------------------------------------------------------------
// accountBalance.js, Phase 53
//
// Helpers for the hybrid account-balance model. Every account has:
//   • balance                , current numeric value shown in the UI
//   • balance_source_filename, the statement filename it came from (NULL if
//                               never set or manually entered)
//   • balance_as_of_date     , period_end date of that statement (or the
//                               date the user manually edited it)
//   • balance_manual         , true when the user last set it by hand
//
// PRECEDENCE
// ----------
// A fresher signal always wins. We update an account's balance only when
// the incoming `as_of_date` is strictly later than the stored one (or when
// there is no stored one). The `balance_manual` flag is informational  
// it tells the UI to show "set manually on…" instead of "from XYZ.pdf".
// ---------------------------------------------------------------------------
import { supabase } from '@/lib/supabaseClient';

/**
 * Sync an account's balance from a freshly-parsed statement.
 *
 * Safe to call repeatedly, it's a no-op if the statement we're handing in
 * is older than the one we already used. Returns one of:
 *   { applied: true,  reason: 'updated', new_balance, new_as_of_date }
 *   { applied: false, reason: 'stale_statement' | 'no_data' | 'error', error? }
 *
 * @param {object} args
 * @param {string} args.account_id           Account UUID
 * @param {number} args.closing_balance      Parsed closing balance (number)
 * @param {string} args.statement_period_end YYYY-MM-DD
 * @param {string} args.filename             Source statement filename
 */
export async function syncAccountBalanceFromStatement({
  account_id, closing_balance, statement_period_end, filename,
}) {
  if (!account_id || closing_balance == null || !statement_period_end) {
    return { applied: false, reason: 'no_data' };
  }
  try {
    const { data: cur, error: readErr } = await supabase
      .from('accounts')
      .select('balance_as_of_date')
      .eq('id', account_id)
      .single();
    if (readErr) {
      // Most likely the new columns haven't been added yet. Don't blow up;
      // just skip the balance sync. The user will see balance=0 until the
      // migration runs.
      console.warn('syncAccountBalanceFromStatement: read failed (migration pending?):', readErr.message);
      return { applied: false, reason: 'error', error: readErr.message };
    }
    const existing = cur?.balance_as_of_date || null;
    if (existing && existing >= statement_period_end) {
      // We already have a statement at least this fresh.
      return { applied: false, reason: 'stale_statement' };
    }
    const { error: updErr } = await supabase
      .from('accounts')
      .update({
        balance: Number(closing_balance),
        balance_source_filename: filename || null,
        balance_as_of_date: statement_period_end,
        balance_manual: false,
      })
      .eq('id', account_id);
    if (updErr) {
      // Same fallback, usually means columns not yet added.
      console.warn('syncAccountBalanceFromStatement: update failed:', updErr.message);
      return { applied: false, reason: 'error', error: updErr.message };
    }
    return {
      applied: true,
      reason: 'updated',
      new_balance: Number(closing_balance),
      new_as_of_date: statement_period_end,
    };
  } catch (e) {
    console.warn('syncAccountBalanceFromStatement threw:', e?.message || e);
    return { applied: false, reason: 'error', error: e?.message };
  }
}

/**
 * Manual balance override. Sets balance_manual=true and stores TODAY as
 * the as-of date.
 */
export async function setAccountBalanceManually({ account_id, balance }) {
  if (!account_id || balance == null || !Number.isFinite(Number(balance))) {
    return { ok: false, error: 'invalid_input' };
  }
  const today = new Date().toISOString().slice(0, 10);
  try {
    const { error } = await supabase
      .from('accounts')
      .update({
        balance: Number(balance),
        balance_source_filename: null,
        balance_as_of_date: today,
        balance_manual: true,
      })
      .eq('id', account_id);
    if (error) return { ok: false, error: error.message };
    return { ok: true };
  } catch (e) {
    return { ok: false, error: e?.message };
  }
}

/**
 * Format a balance-provenance subtitle for display under the account card.
 * Returns null when there's nothing meaningful to show (never set).
 */
export function formatBalanceProvenance(account) {
  if (!account) return null;
  const asOf = account.balance_as_of_date;
  if (!asOf) return null;
  // Tiny date formatter, keep it inline so we don't drag in date-fns just
  // for this single use.
  const d = new Date(`${asOf}T00:00:00Z`);
  const niceDate = d.toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
  if (account.balance_manual) {
    return `Set manually · ${niceDate}`;
  }
  if (account.balance_source_filename) {
    // Trim long filenames so the card doesn't break.
    const stem = String(account.balance_source_filename).replace(/\.[^.]+$/, '');
    const short = stem.length > 28 ? `${stem.slice(0, 26)}…` : stem;
    return `From ${short} · ${niceDate}`;
  }
  return `As of ${niceDate}`;
}
